var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from "react";
import { Draggable } from "react-beautiful-dnd";
import classNames from "classnames";
import CustomCard from "../../UI/CustomCard/CustomCard";
import ProfilePicture from "../Person/ProfilePicture/ProfilePicture";
import RelativeDate from "../../UI/RelativeDate/RelativeDate";
import ProgressBar from "../../UI/ProgressBar/ProgressBar";
import FAIcon from "../../UI/FAIcon/FAIcon";
import SingletonService from "../../../Services/Shared/Singleton.service";
import OpportunityDomainService from "../../../Domain/Opportunity/Opportunity.service";
const DURATION_MARKERS = SingletonService.makeSingleton(() => {
    const markers = [
        {
            // three days
            durationMs: 1000 * 60 * 60 * 24 * 3,
            icon: _jsx(FAIcon, { icon: "minus" }),
            topOffset: 8,
            color: "success",
            hoverText: "This opportunity has been in this stage for three days or less",
        },
        {
            // two weeks
            durationMs: 1000 * 60 * 60 * 24 * 7 * 2,
            icon: _jsx(FAIcon, { icon: "angle-up" }),
            topOffset: 8,
            color: "warning",
            hoverText: "This opportunity has been in this stage for two weeks or less",
        },
        {
            // for the rest of time
            durationMs: Infinity,
            icon: _jsx(FAIcon, { icon: "tilde" }),
            topOffset: 8,
            color: "danger",
            boldDate: true,
            hoverText: "This opportunity has been in this stage for longer than two weeks",
        },
    ];
    markers.sort((a, b) => a.durationMs - b.durationMs);
    return markers;
});
const getMarker = (updatedDate) => {
    const timeMs = new Date().getTime() - updatedDate.getTime();
    for (const duration of DURATION_MARKERS()) {
        if (duration.durationMs > timeMs) {
            return duration;
        }
    }
    return null;
};
const StageUpdatedDate = ({ stageUpdatedDate }) => {
    const marker = getMarker(stageUpdatedDate);
    return (_jsxs("small", { className: classNames("d-flex align-items-center", {
            "font-weight-bold": marker === null || marker === void 0 ? void 0 : marker.boldDate,
        }), style: { position: "relative" }, children: [marker && (_jsx("div", { className: "text-" + marker.color, style: {
                    position: "absolute",
                    top: -marker.topOffset,
                    width: "100%",
                    textAlign: "center",
                }, title: marker.hoverText, children: marker.icon })), _jsx(RelativeDate, { date: stageUpdatedDate, compact: true })] }));
};
const OpportunityCard = ({ opportunity, index, dragDisabled, stage, onClick, className, stagesInPipeline, }) => {
    // @ts-expect-error - TS7006 - Parameter 'dndStyles' implicitly has an 'any' type. | TS7006 - Parameter 'snapshot' implicitly has an 'any' type. | TS7006 - Parameter 'draggableId' implicitly has an 'any' type.
    function getDraggableStyle(dndStyles, snapshot, draggableId) {
        // stop draggables from moving.
        if (!snapshot.isDragging) {
            return { position: "relative" };
        }
        // Don't animate opportunities that are dropped on the same stage.
        if (snapshot.isDropAnimating && snapshot.draggingOver === draggableId) {
            return {};
        }
        return Object.assign(Object.assign({}, dndStyles), { 
            // cannot be 0, but make it super tiny
            transitionDuration: `0.1s` });
    }
    return (_jsx(Draggable, { index: index, draggableId: opportunity.id.toString(), isDragDisabled: !!dragDisabled, children: (dragProvided, dragSnapshot) => {
            var _a;
            return (_jsx(_Fragment, { children: _jsxs(CustomCard, Object.assign({ customHeader: _jsxs("div", { className: "card-header px-2 bg-white d-flex justify-content-between", children: [_jsxs("div", { className: "d-flex align-items-center", children: [_jsx(ProfilePicture, { person: opportunity.subject, showProfilePicture: true, size: 20 }), _jsx("div", { className: "ml-2 h6 mb-0", style: {
                                            overflow: "hidden",
                                            maxWidth: "20ch",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                        }, children: opportunity.subject.fullName })] }), _jsx(StageUpdatedDate, { stageUpdatedDate: opportunity.stageUpdatedDate })] }), noShadow: !dragSnapshot.isDragging, ref: dragProvided.innerRef }, dragProvided.draggableProps, dragProvided.dragHandleProps, { 
                    // @ts-expect-error - TS2322 - Type 'string | null | undefined' is not assignable to type 'string | undefined'.
                    className: className, style: getDraggableStyle(dragProvided.draggableProps.style, dragSnapshot, stage.id.toString()), onClick: () => {
                        if (onClick) {
                            onClick();
                        }
                    }, children: [_jsxs("div", { className: "p-2 d-flex justify-content-between", children: [_jsx("div", { className: "d-flex align-items-top h-100 flex-column", children: _jsx("small", { children: _jsx("span", { className: "badge badge-secondary badge-sm badge-pill", title: "Opportunity Source", children: (_a = opportunity.opportunitySource) === null || _a === void 0 ? void 0 : _a.name }) }) }), _jsx("div", { className: "d-flex flex-column justify-content-end h-100", style: { minHeight: "100%" }, children: opportunity.assignedTo && (_jsx("div", { className: "d-flex", children: _jsx(ProfilePicture, { person: opportunity.assignedTo, showProfilePicture: true, size: 25, title: "Assigned to: " + opportunity.assignedTo.fullName }) })) })] }), _jsx("div", { className: "w-100", style: { position: "absolute", bottom: 0 }, children: _jsx(ProgressBar, { height: 3, bordersBetweenBars: true, bars: OpportunityDomainService.getProgressBars({
                                    stagesInPipeline,
                                    opportunityVisitedStageIds: opportunity.visitedStageIds,
                                    currentStageId: opportunity.stage.id,
                                }).map((_a) => {
                                    var { label } = _a, others = __rest(_a, ["label"]);
                                    return others;
                                }) }) })] })) }));
        } }));
};
export default OpportunityCard;
